import React, {
  ButtonHTMLAttributes,
  forwardRef,
  Fragment,
  isValidElement,
} from "react"
import { Slot, Slottable } from "@radix-ui/react-slot"

import { ResolvedApplicationTreeItem } from "@/lib/kontent/config/getApplicationTree"
import { cn } from "@/lib/utils"
import {
  baseButtonVariants,
  IBaseButtonVariantProps,
  RemixIcon,
} from "@/components/ui/atoms"

export type ButtonProps = IBaseButtonVariantProps &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    asChild?: boolean
  }

export type TButton = {
  codename: string
  ctaLabel: string
  ctaLink: string
  linkedPage?: ResolvedApplicationTreeItem
  style: string
  iconRight?: string | null
  iconLeft?: string | null
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      children,
      variant,
      reverse,
      iconLeft,
      iconRight,
      asChild = false,
      ...props
    },
    ref
  ) => {
    /*
      trow error if icon & asChild props are used at the same time
    */
    if ((iconLeft || iconRight) && asChild) {
      throw new Error(
        "You can't use icon and asChild props at the same time. Please choose one." +
          "If you want to use icon, you can use component RemixIcon as child of Button component."
      )
    }
    /*
      trow error if Fragment is used with asChild prop
    */
    if (isValidElement(children) && children.type === Fragment && asChild) {
      throw new Error(
        "You can't use a Fragment with the asChild prop." +
          "In this case, you can use the Slot component as child of Button component."
      )
    }

    const Component = asChild ? Slot : "button"

    /*
      As we use a Fragment to wrap the content, we need to use the Slottable component to make the content slottable. See https://github.com/radix-ui/primitives/blob/main/packages/react/slot/src/Slot.tsx
    */

    return (
      <Component
        className={cn(baseButtonVariants({ variant, reverse, className }), {
          "gap-3": (iconLeft || iconRight) && children,
          "baseButton--icon": (iconLeft || iconRight) && !children,
          "baseButton--icon-large": (iconLeft || iconRight) && !children,
        })}
        ref={ref}
        {...props}
      >
        {iconLeft ? (
          <RemixIcon
            name={iconLeft}
            size="sm"
            className="shrink-0 fill-current"
          />
        ) : null}
        {asChild ? <Slottable>{children}</Slottable> : children}
        {iconRight ? (
          <RemixIcon
            name={iconRight}
            size="sm"
            className="shrink-0 fill-current"
          />
        ) : null}
      </Component>
    )
  }
)
Button.displayName = "Button"

export { Button }
